import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Progress,
  SimpleGrid,
  Skeleton,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { type ReactNode, useState } from "react"
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  type DashboardDocumentTotalsDTO,
  type DashboardPeriod,
  type DashboardProductionDTO,
  type DashboardPurchaseOrderRealizationDTO,
  type DashboardSalesOfferRealizationDTO,
  getOperationalDashboard,
} from "../../../api/dashboard"
import { chartLayout, useChartTheme } from "../shared/chartConfig"
import { WmsPageHeader } from "../shared/WmsPageHeader"
import { WmsPagePanel, WmsPageShell } from "../shared/WmsPageShell"

type DocumentChartCardProps = {
  accent: string
  subtitle: string
  title: string
  value: DashboardDocumentTotalsDTO
}

type RealizationCardProps = {
  accent: string
  subtitle: string
  title: string
  value:
    | DashboardPurchaseOrderRealizationDTO
    | DashboardSalesOfferRealizationDTO
}

type ProductionCardProps = {
  value: DashboardProductionDTO
}

// WmsOperationalDashboard renders the main operational KPI screen for WMS.
export function WmsOperationalDashboard() {
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const errorBg = useColorModeValue("red.50", "red.900")
  const errorBorder = useColorModeValue("red.200", "red.700")
  const [period, setPeriod] = useState<DashboardPeriod>("month")

  const query = useQuery({
    queryKey: ["wms", "dashboard", "operational", period],
    queryFn: () => getOperationalDashboard(period),
    retry: false,
  })

  const data = query.data
  const totalIsEmpty =
    !!data &&
    data.purchases.total_qty === 0 &&
    data.sales.total_qty === 0 &&
    data.purchase_order_realization.requested_qty === 0 &&
    data.sales_offer_realization.offered_qty === 0 &&
    data.production.open_target_qty === 0 &&
    data.production.closed_produced_qty === 0

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Dashboard operacyjny"
        description="Przekroj zakupow, sprzedazy, realizacji dokumentow i produkcji dla biezacego okresu."
        gradient="linear(to-r, green.600, teal.700)"
        badge={
          data ? (
            <Badge
              bg="whiteAlpha.300"
              color="white"
              borderRadius="full"
              px={3}
              py={1}
            >
              {data.period_label} |{" "}
              {formatDateRange(data.period_start, data.period_end)}
            </Badge>
          ) : (
            "Dashboard"
          )
        }
        actions={
          <ButtonGroup isAttached variant="outline" size="sm">
            <PeriodButton current={period} value="month" onSelect={setPeriod}>
              Miesiac
            </PeriodButton>
            <PeriodButton current={period} value="quarter" onSelect={setPeriod}>
              Kwartal
            </PeriodButton>
            <PeriodButton current={period} value="year" onSelect={setPeriod}>
              Rok
            </PeriodButton>
          </ButtonGroup>
        }
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <Flex
          direction="column"
          gap={4}
          flex="1"
          minH={0}
          overflowY="auto"
          pr={{ base: 0, lg: 1 }}
        >
          {query.isError ? (
            <Box
              borderWidth="1px"
              borderColor={errorBorder}
              bg={errorBg}
              borderRadius="xl"
              p={4}
            >
              <Heading size="sm" mb={1}>
                Nie udalo sie pobrac dashboardu
              </Heading>
              <Text fontSize="sm">
                {(query.error as Error)?.message ?? "Blad pobierania danych."}
              </Text>
            </Box>
          ) : query.isPending ? (
            <DashboardSkeleton />
          ) : totalIsEmpty ? (
            <Box
              borderWidth="1px"
              borderColor={panelBorder}
              borderRadius="xl"
              p={5}
            >
              <Heading size="sm" mb={2}>
                Brak danych dla wybranego okresu
              </Heading>
              <Text color={mutedText} fontSize="sm">
                Dashboard jest gotowy, ale w tym przedziale nie ma jeszcze
                potwierdzonych dokumentow ani produkcji.
              </Text>
            </Box>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                <DocumentChartCard
                  title="Zakup"
                  subtitle="Potwierdzone PZ"
                  value={data!.purchases}
                  accent="#2F855A"
                />
                <DocumentChartCard
                  title="Sprzedaz"
                  subtitle="Potwierdzone WZ"
                  value={data!.sales}
                  accent="#2B6CB0"
                />
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                <RealizationCard
                  title="Realizacja ZZ -> PZ"
                  subtitle="Stopien zamkniecia zamowien zakupu"
                  value={data!.purchase_order_realization}
                  accent="#D69E2E"
                />
                <RealizationCard
                  title="Realizacja OS -> WZ"
                  subtitle="Potwierdzone wydania z ofert"
                  value={data!.sales_offer_realization}
                  accent="#DD6B20"
                />
              </SimpleGrid>

              <ProductionCard value={data!.production} />
            </>
          )}
        </Flex>
      </WmsPagePanel>
    </WmsPageShell>
  )
}

// PeriodButton renders one period toggle item.
function PeriodButton({
  children,
  current,
  onSelect,
  value,
}: {
  children: ReactNode
  current: DashboardPeriod
  onSelect: (value: DashboardPeriod) => void
  value: DashboardPeriod
}) {
  return (
    <Button
      variant={current === value ? "solid" : "outline"}
      colorScheme={current === value ? "teal" : undefined}
      onClick={() => onSelect(value)}
    >
      {children}
    </Button>
  )
}

// DashboardSkeleton keeps layout stable while KPI data is loading.
function DashboardSkeleton() {
  return (
    <Flex direction="column" gap={4}>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        <Skeleton h="360px" borderRadius="xl" />
        <Skeleton h="360px" borderRadius="xl" />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        <Skeleton h="320px" borderRadius="xl" />
        <Skeleton h="320px" borderRadius="xl" />
      </SimpleGrid>
      <Skeleton h="260px" borderRadius="xl" />
    </Flex>
  )
}

// DocumentChartCard renders a daily distribution line chart for purchase or sales flow.
function DocumentChartCard({
  accent,
  subtitle,
  title,
  value,
}: DocumentChartCardProps) {
  const { axisStroke, tooltipBg, tooltipBorder, tooltipText } = useChartTheme()
  const [metric, setMetric] = useState<"netto" | "brutto" | "qty" | "count">(
    "netto",
  )
  const chartData =
    value.daily_points && value.daily_points.length > 0
      ? value.daily_points
      : []

  return (
    <Box borderWidth="1px" borderColor="inherit" borderRadius="xl" p={4}>
      <Flex justify="space-between" align="flex-start" mb={3} gap={3}>
        <Box>
          <Heading size="md">{title}</Heading>
          <Text fontSize="sm" color="gray.500">
            {subtitle}
          </Text>
        </Box>
        <Badge
          bg={`${accent}22`}
          color={accent}
          borderRadius="full"
          px={3}
          py={1}
        >
          {value.documents_count} dok.
        </Badge>
      </Flex>

      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} mb={4}>
        <MiniStat label="Ilosc" value={formatNumber(value.total_qty)} />
        <MiniStat
          label="Wartosc netto"
          value={formatMoney(value.total_value_netto)}
        />
        <MiniStat
          label="Wartosc brutto"
          value={formatMoney(value.total_value_brutto)}
        />
      </SimpleGrid>

      <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="gray.500"
          textTransform="uppercase"
        >
          Rozklad dzienny
        </Text>
        <ButtonGroup isAttached size="xs" variant="outline">
          <Button
            variant={metric === "netto" ? "solid" : "outline"}
            colorScheme={metric === "netto" ? "teal" : undefined}
            onClick={() => setMetric("netto")}
          >
            Netto (zl)
          </Button>
          <Button
            variant={metric === "brutto" ? "solid" : "outline"}
            colorScheme={metric === "brutto" ? "teal" : undefined}
            onClick={() => setMetric("brutto")}
          >
            Brutto (zl)
          </Button>
          <Button
            variant={metric === "qty" ? "solid" : "outline"}
            colorScheme={metric === "qty" ? "teal" : undefined}
            onClick={() => setMetric("qty")}
          >
            Ilosc (szt.)
          </Button>
          <Button
            variant={metric === "count" ? "solid" : "outline"}
            colorScheme={metric === "count" ? "teal" : undefined}
            onClick={() => setMetric("count")}
          >
            Dokumenty
          </Button>
        </ButtonGroup>
      </Flex>

      <Box h={chartLayout.height}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={chartLayout.margin}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: axisStroke }}
              interval="preserveStartEnd"
              minTickGap={15}
            />
            <YAxis tick={{ fontSize: 11, fill: axisStroke }} />
            <Tooltip
              contentStyle={{
                background: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: "8px",
                color: tooltipText,
              }}
              labelStyle={{ color: tooltipText, fontWeight: "bold" }}
              itemStyle={{ color: tooltipText }}
              labelFormatter={(label, payload) => {
                const item = payload?.[0]?.payload
                return item ? `Dzien: ${item.date} (${item.label})` : label
              }}
              formatter={
                ((raw: number | string | undefined) => {
                  const numeric =
                    typeof raw === "number" ? raw : Number(raw ?? 0)
                  if (metric === "netto")
                    return [formatMoney(numeric), "Wartosc netto"]
                  if (metric === "brutto")
                    return [formatMoney(numeric), "Wartosc brutto"]
                  if (metric === "qty") return [formatNumber(numeric), "Ilosc"]
                  return [String(numeric), "Dokumenty"]
                }) as any
              }
            />
            <Line
              type="linear"
              dataKey={
                metric === "netto"
                  ? "total_value_netto"
                  : metric === "brutto"
                    ? "total_value_brutto"
                    : metric === "qty"
                      ? "total_qty"
                      : "documents_count"
              }
              stroke={accent}
              strokeWidth={2.5}
              dot={{ r: chartData.length > 40 ? 1 : 3, fill: accent }}
              activeDot={{ r: 6, fill: accent }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}

// RealizationCard renders a percentage diagram and headline counts for one flow.
function RealizationCard({
  accent,
  subtitle,
  title,
  value,
}: RealizationCardProps) {
  const { tooltipBg, tooltipBorder, tooltipText } = useChartTheme()
  const donutEmptyColor = useColorModeValue("#E2E8F0", "#4A5568")
  const percent = Math.max(0, Math.min(100, value.realization_percent))
  const remainingPercent = Math.max(0, 100 - percent)
  const pieData = [
    { name: "Zrealizowane", value: percent, color: accent },
    { name: "Pozostalo", value: remainingPercent, color: donutEmptyColor },
  ]

  const isZZ = "orders_count" in value
  const denominator = isZZ ? value.requested_qty : value.offered_qty
  const numerator = isZZ ? value.realized_qty : value.released_qty
  const docTotal = isZZ ? value.orders_count : value.offers_count

  const statusColorScheme =
    percent >= 80
      ? "green"
      : percent >= 50
        ? "teal"
        : percent >= 25
          ? "yellow"
          : "orange"
  const statusText =
    percent >= 80
      ? "Wysoki KPI"
      : percent >= 50
        ? "Dobra realizacja"
        : percent >= 25
          ? "W toku"
          : "Wymaga uwagi"

  return (
    <Box borderWidth="1px" borderColor="inherit" borderRadius="xl" p={4}>
      <Flex justify="space-between" align="flex-start" mb={3} gap={3}>
        <Box>
          <Heading size="md">{title}</Heading>
          <Text fontSize="sm" color="gray.500">
            {subtitle}
          </Text>
        </Box>
        <Badge
          colorScheme={statusColorScheme}
          borderRadius="full"
          px={3}
          py={1}
          fontSize="xs"
          fontWeight="bold"
        >
          {statusText} | {formatPercent(percent)}
        </Badge>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} alignItems="center">
        <Box position="relative" h="220px">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                innerRadius={62}
                outerRadius={84}
                paddingAngle={3}
                strokeWidth={0}
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: tooltipBg,
                  border: `1px solid ${tooltipBorder}`,
                  borderRadius: "8px",
                  color: tooltipText,
                }}
                labelStyle={{ color: tooltipText }}
                itemStyle={{ color: tooltipText }}
                formatter={
                  ((raw: number | string | undefined) => [
                    formatPercent(
                      typeof raw === "number" ? raw : Number(raw ?? 0),
                    ),
                    "Udzial",
                  ]) as any
                }
              />
            </PieChart>
          </ResponsiveContainer>
          <Flex
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            direction="column"
            align="center"
            justify="center"
            pointerEvents="none"
          >
            <Text fontSize="2xl" fontWeight="extrabold" lineHeight="1">
              {formatPercent(percent)}
            </Text>
            <Text
              fontSize="xs"
              color="gray.500"
              textTransform="uppercase"
              mt={1}
            >
              KPI Realizacji
            </Text>
          </Flex>
        </Box>

        <Flex direction="column" gap={3}>
          <Box>
            <Flex justify="space-between" align="center" mb={1}>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="gray.500"
                textTransform="uppercase"
              >
                Wolumen ilosciowy
              </Text>
              <Text fontSize="xs" fontWeight="bold">
                {formatNumber(numerator)} / {formatNumber(denominator)}
              </Text>
            </Flex>
            <Progress
              value={percent}
              colorScheme={statusColorScheme}
              borderRadius="full"
              h="10px"
            />
          </Box>

          <SimpleGrid columns={2} spacing={2}>
            <MiniStat
              label={isZZ ? "Dokumenty ZZ" : "Dokumenty OS"}
              value={String(docTotal)}
            />
            {isZZ ? (
              <MiniStat
                label="Zrealizowane ZZ"
                value={`${value.realized_count} (${formatPercent(ratioPercent(value.realized_count, value.orders_count))})`}
              />
            ) : (
              <MiniStat
                label="Oferty z WZ"
                value={`${value.realized_offers_count} (${formatPercent(ratioPercent(value.realized_offers_count, value.offers_count))})`}
              />
            )}
            {isZZ ? (
              <MiniStat
                label="Czesciowe ZZ"
                value={String(value.partially_realized_count)}
              />
            ) : (
              <MiniStat
                label="Wydana ilosc"
                value={formatNumber(value.released_qty)}
              />
            )}
            {isZZ ? (
              <MiniStat
                label="Oczekujace ZZ"
                value={String(
                  Math.max(
                    0,
                    value.orders_count -
                      value.realized_count -
                      value.partially_realized_count,
                  ),
                )}
              />
            ) : (
              <MiniStat
                label="Pozostala ilosc"
                value={formatNumber(
                  Math.max(0, value.offered_qty - value.released_qty),
                )}
              />
            )}
          </SimpleGrid>
        </Flex>
      </SimpleGrid>
    </Box>
  )
}

// ProductionCard summarizes open production progress, closed output value, and daily distribution trends.
function ProductionCard({ value }: ProductionCardProps) {
  const { axisStroke, tooltipBg, tooltipBorder, tooltipText } = useChartTheme()
  const openPercent = Math.max(0, Math.min(100, value.open_progress_percent))
  const [metric, setMetric] = useState<"netto" | "qty">("netto")
  const chartData =
    value.daily_points && value.daily_points.length > 0
      ? value.daily_points
      : []

  const openRemainingQty = Math.max(
    0,
    value.open_target_qty - value.open_produced_qty,
  )
  const avgPerClosedOrder =
    value.closed_orders_count > 0
      ? value.closed_produced_qty / value.closed_orders_count
      : 0

  return (
    <Box borderWidth="1px" borderColor="inherit" borderRadius="xl" p={4}>
      <Flex
        justify="space-between"
        align="flex-start"
        mb={4}
        gap={3}
        wrap="wrap"
      >
        <Box>
          <Heading size="md">Produkcja i Wykonanie</Heading>
          <Text fontSize="sm" color="gray.500">
            Otwarte zlecenia, postep realizacji oraz dzienny rozklad zamknietej
            produkcji.
          </Text>
        </Box>
        <Badge
          colorScheme="teal"
          borderRadius="full"
          px={3}
          py={1}
          fontSize="xs"
          fontWeight="bold"
        >
          {value.open_orders_count} otwartych | {value.closed_orders_count}{" "}
          zamknietych
        </Badge>
      </Flex>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        <Box borderWidth="1px" borderColor="inherit" borderRadius="xl" p={4}>
          <Heading size="sm" mb={3}>
            Wskazniki Realizacji Produkcji
          </Heading>

          <Box mb={4}>
            <Flex justify="space-between" align="center" mb={1}>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="gray.500"
                textTransform="uppercase"
              >
                Postep otwartych zlecen
              </Text>
              <Text fontSize="xs" fontWeight="bold">
                {formatPercent(openPercent)} (
                {formatNumber(value.open_produced_qty)} /{" "}
                {formatNumber(value.open_target_qty)})
              </Text>
            </Flex>
            <Progress
              value={openPercent}
              colorScheme="teal"
              borderRadius="full"
              h="12px"
            />
          </Box>

          <SimpleGrid columns={2} spacing={3} mb={4}>
            <MiniStat
              label="Otwarte zlecenia"
              value={String(value.open_orders_count)}
            />
            <MiniStat
              label="Planowana ilosc"
              value={formatNumber(value.open_target_qty)}
            />
            <MiniStat
              label="Wyprodukowano"
              value={formatNumber(value.open_produced_qty)}
            />
            <MiniStat
              label="Pozostalo szt."
              value={formatNumber(openRemainingQty)}
            />
          </SimpleGrid>

          <Heading size="xs" textTransform="uppercase" color="gray.500" mb={2}>
            Podsumowanie zamknietej produkcji
          </Heading>
          <SimpleGrid columns={2} spacing={3}>
            <MiniStat
              label="Zamkniete zlecenia"
              value={String(value.closed_orders_count)}
            />
            <MiniStat
              label="Wyprodukowana ilosc"
              value={formatNumber(value.closed_produced_qty)}
            />
            <MiniStat
              label="Wartosc netto"
              value={formatMoney(value.closed_production_value_netto)}
            />
            <MiniStat
              label="Srednia na zlecenie"
              value={`${formatNumber(avgPerClosedOrder)} szt.`}
            />
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderColor="inherit" borderRadius="xl" p={4}>
          <Flex
            justify="space-between"
            align="center"
            mb={3}
            wrap="wrap"
            gap={2}
          >
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="gray.500"
              textTransform="uppercase"
            >
              Rozklad dzienny produkcji
            </Text>
            <ButtonGroup isAttached size="xs" variant="outline">
              <Button
                variant={metric === "netto" ? "solid" : "outline"}
                colorScheme={metric === "netto" ? "teal" : undefined}
                onClick={() => setMetric("netto")}
              >
                Netto (zl)
              </Button>
              <Button
                variant={metric === "qty" ? "solid" : "outline"}
                colorScheme={metric === "qty" ? "teal" : undefined}
                onClick={() => setMetric("qty")}
              >
                Ilosc (szt.)
              </Button>
            </ButtonGroup>
          </Flex>

          <Box h="260px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={chartLayout.margin}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: axisStroke }}
                  interval="preserveStartEnd"
                  minTickGap={15}
                />
                <YAxis tick={{ fontSize: 11, fill: axisStroke }} />
                <Tooltip
                  contentStyle={{
                    background: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: "8px",
                    color: tooltipText,
                  }}
                  labelStyle={{ color: tooltipText, fontWeight: "bold" }}
                  itemStyle={{ color: tooltipText }}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload
                    return item ? `Dzien: ${item.date} (${item.label})` : label
                  }}
                  formatter={
                    ((raw: number | string | undefined) => {
                      const numeric =
                        typeof raw === "number" ? raw : Number(raw ?? 0)
                      if (metric === "netto")
                        return [formatMoney(numeric), "Wartosc netto"]
                      return [formatNumber(numeric), "Wyprodukowano"]
                    }) as any
                  }
                />
                <Line
                  type="linear"
                  dataKey={
                    metric === "netto"
                      ? "production_value_netto"
                      : "produced_qty"
                  }
                  stroke="#319795"
                  strokeWidth={2.5}
                  dot={{ r: chartData.length > 40 ? 1 : 3, fill: "#319795" }}
                  activeDot={{ r: 6, fill: "#319795" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </SimpleGrid>
    </Box>
  )
}

// MiniStat renders a compact metric block reused across dashboard cards.
function MiniStat({ label, value }: { label: string; value: string }) {
  const statBg = useColorModeValue("gray.50", "whiteAlpha.100")
  return (
    <Box bg={statBg} borderRadius="lg" px={3} py={2}>
      <Text
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="0.04em"
        color="gray.500"
      >
        {label}
      </Text>
      <Text fontWeight="bold" mt={1}>
        {value}
      </Text>
    </Box>
  )
}

// formatMoney formats dashboard currency values as PLN.
function formatMoney(value: number) {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    maximumFractionDigits: 2,
  }).format(value || 0)
}

// formatNumber formats dashboard quantities with compact Polish decimals.
function formatNumber(value: number) {
  return new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value || 0)
}

// formatPercent formats percentages with one compact decimal.
function formatPercent(value: number) {
  return `${new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value || 0)}%`
}

// ratioPercent calculates a percentage ratio with zero safety.
function ratioPercent(numerator: number, denominator: number) {
  if (!denominator || denominator <= 0) return 0
  return (numerator / denominator) * 100
}

// formatDateRange prints the returned dashboard range in one human-readable label.
function formatDateRange(periodStart: string, periodEnd: string) {
  const start = new Date(periodStart)
  const endExclusive = new Date(periodEnd)
  const endInclusive = new Date(endExclusive.getTime() - 24 * 60 * 60 * 1000)
  return `${start.toLocaleDateString("pl-PL")} - ${endInclusive.toLocaleDateString("pl-PL")}`
}
